<template>
  <div class="session-overview">
    <CdrGrid class="session-overview__header">
      <h2 class="session-overview__title">Overview</h2>
      <CdrList
        class="session-overview__list session-overview__list--social-icons"
        modifier="inline"
      >
        <li class="session-overview__list-item">
          <CdrLink
            :href="facebookUri"
            title="Share on Facebook"
            class="session-overview__social-icon"
            target="_blank"
            rel="nofollow external"
            aria-label="Share on facebook"
            data-ui="share-on-facebook"
            @click="socialMetrics"
          >
            <IconFacebook />
          </CdrLink>
        </li>
        <li class="session-overview__list-item">
          <CdrLink
            :href="pinterestUri"
            title="Share on Pinterest"
            class="session-overview__social-icon"
            target="_blank"
            rel="nofollow external"
            aria-label="Share on pinterest"
            data-ui="share-on-pinterest"
            @click="socialMetrics"
          >
            <IconPinterest />
          </CdrLink>
        </li>
        <li class="session-overview__list-item">
          <AddToCalendar
            v-if="calendarData"
            :modal-data="calendarData"
            :btn-class="`session-overview__calendar-btn`"
            data-ui="add-to-calendar"
          />
        </li>
      </CdrList>
    </CdrGrid>
    <div class="session-overview__content">
      <CdrList
        :modifier="listModifier"
        class="session-overview__list session-overview__list--attributes"
      >
        <CdrText
          v-for="{ type, level, heading, subheading, tip } in courseAttributes"
          :key="type"
          :data-ui="`${type}-attribute`"
          :class="['session-overview__list-item', { 'session-overview__list-item--tip': tip }]"
          tag="li"
        >
          <div
            :class="[
              'course-attributes__heading-container',
              { 'course-attributes__heading-container--has-icon': tip },
            ]"
          >
            <span class="course-attributes__heading">{{ heading }}</span>
            <span
              v-if="tip"
              class="course-attributes__level"
              data-ui="course-attributes-level"
            >
              {{ level }}
            </span>
            <span class="course-attributes__subheading">{{ subheading }}</span>
            <CdrButton
              v-if="tip"
              :aria-label="`More information about ${heading}`"
              :data-type="type"
              :data-ui="`${type}-modal-trigger`"
              :icon-only="true"
              class="course-attributes__modal-trigger"
              aria-haspopup="dialog"
              @click="(event) => openModal({ event, type })"
            >
              <template #icon>
                <IconInformationStroke
                  size="small"
                  class="course-attributes__info-icon"
                  aria-hidden="true"
                />
              </template>
            </CdrButton>
          </div>
        </CdrText>
      </CdrList>
      <CdrText
        class="session-overview__summary-text"
        data-ui="session-overview-summary-text"
      >
        {{ longDescription }}
      </CdrText>
      <CdrList
        modifier="unordered"
        class="session-overview__summary-list"
      >
        <li
          v-for="outcome in outcomes"
          :key="outcome"
          data-ui="session-overview-summary-list-item"
        >
          {{ outcome }}
        </li>
      </CdrList>
      <div
        v-if="showGearlist || displayLegalDetails"
        class="session-overview__gear"
      >
        <h3 class="session-overview__gear-title">What to bring with you</h3>
        <SessionGearlistComponent
          v-if="showGearlist"
          :required="gearlistRequiredItems"
          :optional="gearlistOptionalItems"
          :provided="gearlistProvidedItems"
          data-ui="gearlist"
        />
        <template v-if="displayLegalDetails">
          <div
            v-for="formItem in displayLegalForms"
            :key="formItem.index"
            data-ui="legal-forms-item"
          >
            <CdrLink
              :href="formItem.uri"
              modifier="standalone"
              class="session-overview__legal-link"
              data-ui="activity-risks-and-release-of-liability-agreement-link"
              rel="nofollow"
              target="_blank"
              @click="legalMetrics"
            >
              <span class="session-overview__legal-text">
                {{ `Activity Risks and Release of Liability Agreement - ${formItem.name}` }}
              </span>
              <IconDownload
                class="session-overview__legal-icon"
                aria-hidden="true"
                inherit-color
              />
            </CdrLink>
          </div>
        </template>
      </div>
      <div
        v-if="notes"
        class="session-overview__notes"
        data-ui="notes"
      >
        <h3 class="session-overview__notes-title">Notes</h3>
        <CdrText class="session-overview__notes-text">{{ notes }}</CdrText>
      </div>
      <SessionRelatedLinksComponent
        v-if="showRelatedLinks"
        :related-links="relatedLinks"
      />
      <CdrModal
        id="attribute-modal"
        :opened="isModalOpen"
        :label="activeTitle"
        :aria-described-by="`${activeType}-description`"
        data-ui="attribute-modal"
        @closed="closeModal"
      >
        <template #title>
          <CdrText
            tag="h3"
            class="session-overview__active-title"
          >
            {{ activeTitle }}
          </CdrText>
        </template>
        <AttributeInfoModalContentComponent
          v-if="isModalOpen"
          v-bind="activeConfig"
        />
      </CdrModal>
    </div>
  </div>
</template>

<script>
import {
  CdrButton,
  CdrGrid,
  CdrLink,
  CdrList,
  CdrModal,
  CdrText,
  IconDownload,
  IconFacebook,
  IconPinterest,
  IconInformationStroke,
} from '@rei/cedar';
import AddToCalendar from '../legacy/components/add-to-calendar/index.vue';
import SessionGearlistComponent from './SessionGearlistComponent.vue';
import SessionRelatedLinksComponent from './SessionRelatedLinksComponent.vue';
import metrics from '../mixins/metrics';
import AttributeInfoModalContentComponent from './AttributeInfoModalContentComponent.vue';
import constants from '../common/constants';

export default {
  name: 'SessionOverviewComponent',
  components: {
    CdrButton,
    CdrGrid,
    CdrLink,
    CdrList,
    CdrModal,
    CdrText,
    IconDownload,
    IconFacebook,
    IconPinterest,
    IconInformationStroke,
    AddToCalendar,
    AttributeInfoModalContentComponent,
    SessionGearlistComponent,
    SessionRelatedLinksComponent,
  },
  mixins: [metrics],
  props: {
    activityLevel: {
      type: [Boolean, Object],
      required: true,
      default: false,
    },
    allActivityLevels: {
      type: Array,
      required: true,
    },
    allSkillLevels: {
      type: Array,
      required: true,
    },
    baseImageUri: {
      type: String,
      required: true,
    },
    briefDescription: {
      type: String,
      required: true,
    },
    courseGearInfo: {
      type: Object,
      required: true,
    },
    courseName: {
      type: String,
      required: true,
    },
    courseAttributes: {
      type: Array,
      required: true,
    },
    deviceSize: {
      type: String,
      required: true,
    },
    displayLocationName: {
      type: String,
      required: true,
    },
    displayLegalForms: {
      type: Array,
      required: true,
    },
    longDescription: {
      type: String,
      required: true,
    },
    outcomes: {
      type: Array,
      required: false,
      default: () => [],
    },
    sessionData: {
      type: Object,
      required: true,
    },
    skillLevel: {
      type: [Boolean, Object],
      required: true,
      default: false,
    },
  },
  data: () => ({
    activeType: '',
    isModalOpen: false,
  }),
  computed: {
    activeConfig() {
      const {
        activeType: type,
        activityLevel,
        allActivityLevels,
        allSkillLevels,
        courseName,
        skillLevel,
      } = this;
      if (!type) return {};
      const config = { courseName, type };
      switch (type) {
        case 'skill-level':
          config.activePayload = skillLevel;
          config.allLevels = allSkillLevels;
          config.blurb = constants.ATTRIBUTE_LEVELS.SKILL.BLURB;
          config.title = constants.ATTRIBUTE_LEVELS.SKILL.TITLE;
          break;
        case 'activity-level':
          config.activePayload = activityLevel;
          config.allLevels = allActivityLevels;
          config.title = constants.ATTRIBUTE_LEVELS.ACTIVITY.TITLE;
          break;
        default:
          break;
      }
      return config;
    },
    activeTitle() {
      const { title = '' } = this.activeConfig || {};
      return title;
    },
    calendarData() {
      const { sessionData, courseName, displayLocationName } = this;
      if (sessionData.status === 'CANCELED') return false;
      const eventStart = sessionData.start;
      const eventEnd = sessionData.end;
      const eventSessionId = sessionData.sessionId;
      const eventTitle = courseName;
      const eventLocationName = displayLocationName;
      const eventUri = sessionData.uri;
      const eventRegistrationStatus = sessionData.registrationRequired;

      return {
        eventStart,
        eventEnd,
        eventSessionId,
        eventTitle,
        eventLocationName,
        eventUri,
        eventRegistrationStatus,
      };
    },
    displayLegalDetails() {
      const { displayLegalForms } = this;
      return Object.keys(displayLegalForms).length > 0;
    },
    facebookUri() {
      const facebookAppId = 131317376894863;
      return `http://www.facebook.com/dialog/feed?app_id=${facebookAppId}&redirect_uri=http://www.facebook.com/&link=https://www.rei.com${this.sessionData.uri}&picture=https://www.rei.com${this.baseImageUri}&name=${this.courseName}&description=${this.briefDescription}`;
    },
    gearlistRequiredItems() {
      return this.getGearlistItems('required', '(Required)');
    },
    gearlistOptionalItems() {
      return this.getGearlistItems('optional', '(Optional)');
    },
    gearlistProvidedItems() {
      return this.getGearlistItems('provided', 'Provided by REI:');
    },
    listModifier() {
      return `${this.deviceSize === 'mobile' ? '' : 'inline'}`;
    },
    optionalField() {
      return this.courseGearInfo.optional.length > 0;
    },
    notes() {
      const { sessionData } = this;
      const notes =
        sessionData && typeof sessionData.notes === 'string' ? sessionData.notes : false;
      return notes;
    },
    pinterestUri() {
      return `http://pinterest.com/pin/create/button/?url=https://www.rei.com${this.sessionData.uri}&media=https://www.rei.com${this.baseImageUri}&description=${this.briefDescription}`;
    },
    providedField() {
      return this.courseGearInfo.provided.length > 0;
    },
    relatedLinks() {
      const {
        sessionData: { partners = [] },
      } = this;
      return partners.map(({ name, uri }, index) => ({
        key: index,
        name: name === 'REGISTER HERE' ? 'Register here' : name,
        uri,
      }));
    },
    requiredField() {
      return this.courseGearInfo.required.length > 0;
    },
    /*
      If even one of these props is an array, return <true>.
    */
    showGearlist() {
      const { gearlistRequiredItems, gearlistOptionalItems, gearlistProvidedItems } = this;
      return [gearlistRequiredItems, gearlistOptionalItems, gearlistProvidedItems].some(
        (item) => item !== false,
      );
    },
    showRelatedLinks() {
      return this.sessionData.partners && this.sessionData.partners.length > 0;
    },
    twitterUri() {
      return `https://twitter.com/intent/tweet?text=${this.courseName}&url=https://www.rei.com${this.sessionData.uri}&related=rei`;
    },
  },
  methods: {
    openModal({ type }) {
      this.activeType = type;
      const linkName = `{{ pageName }}_${type.replace('-', ' ')} modal_open`;
      this.metricsClick({ linkName });
      this.$nextTick(() => {
        this.isModalOpen = true;
      });
    },
    closeModal() {
      const { activeType: type } = this;
      this.isModalOpen = false;
      const linkName = `{{ pageName }}_${type.replace('-', ' ')} modal_close`;
      this.metricsClick({ linkName });
      this.$nextTick(() => {
        this.activeType = '';
      });
    },
    getGearlistItems(type, title) {
      const { courseGearInfo = {} } = this;
      const data = courseGearInfo[type];
      let payload = false;
      if (data.length > 0) {
        payload = {
          items: data.map((text, index) => ({ key: index, text })),
          title,
          type,
        };
      }
      return payload;
    },
    socialMetrics({ currentTarget }) {
      const socialTitle = currentTarget.title;
      const linkName = `{{ pageName }}_social icons_${socialTitle}`;
      this.metricsClick({ linkName });
    },
    legalMetrics({ currentTarget }) {
      const legalFormTitle = currentTarget.text;
      const linkName = `{{ pageName }}_forms_${legalFormTitle}`;
      this.metricsClick({ linkName });
    },
  },
};
</script>
